import { useState, useEffect, type FormEvent } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { api } from '../api/public';
import s from './Contact.module.css';

const FALLBACK = { email: 'support@jiayin.jp', phone: '03-XXXX-XXXX', hours: '平日 9:00 - 18:00' };

export function Contact() {
  const ref = useScrollReveal<HTMLElement>();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [support, setSupport] = useState(FALLBACK);

  useEffect(() => {
    fetch('/api/public/settings')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setSupport({ email: data.support_email, phone: data.support_phone, hours: data.support_hours });
      })
      .catch(() => {});
  }, []);

  const update = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.submitContact(form);
      setSuccess(true);
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : '送信に失敗しました。しばらく経ってからお試しください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className={`${s.section} reveal`} ref={ref}>
      <div className={s.inner}>
        <h2 className={s.title}>お問い合わせ</h2>
        <div className={s.wrapper}>
          <div className={s.info}>
            <h3 className={s.infoTitle}>お気軽にご相談ください</h3>
            <p className={s.infoDesc}>
              導入に関するご質問、デモのご依頼、お見積りなど、
              どんなことでもお気軽にお問い合わせください。
              担当者より2営業日以内にご連絡いたします。
            </p>
            <div className={s.infoItem}>
              <span className={s.infoIcon}>&#9993;</span>
              <span>{support.email}</span>
            </div>
            <div className={s.infoItem}>
              <span className={s.infoIcon}>&#9742;</span>
              <span>{support.phone}（{support.hours}）</span>
            </div>
          </div>

          {success ? (
            <div className={s.success}>
              <span>お問い合わせありがとうございます。<br />担当者より2営業日以内にご連絡いたします。</span>
              <button className={s.closeBtn} onClick={() => setSuccess(false)} title="閉じる">{'\u2715'}</button>
            </div>
          ) : (
            <form className={s.form} onSubmit={handleSubmit}>
              <div className={s.field}>
                <label className={s.label}>お名前<span className={s.labelOptional}>（必須）</span></label>
                <input className={s.input} type="text" value={form.name} onChange={(e) => update('name', e.target.value)} required />
              </div>
              <div className={s.field}>
                <label className={s.label}>メールアドレス<span className={s.labelOptional}>（必須）</span></label>
                <input className={s.input} type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
              </div>
              <div className={s.field}>
                <label className={s.label}>会社名<span className={s.labelOptional}>（任意）</span></label>
                <input className={s.input} type="text" value={form.company} onChange={(e) => update('company', e.target.value)} />
              </div>
              <div className={s.field}>
                <label className={s.label}>メッセージ<span className={s.labelOptional}>（必須）</span></label>
                <textarea className={s.textarea} value={form.message} onChange={(e) => update('message', e.target.value)} required />
              </div>
              {error && <div className={s.error}>{error}</div>}
              <button className={s.submit} type="submit" disabled={loading}>
                {loading ? '送信中...' : '送信する'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
